import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  FolderOpen,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { useData } from '../lib/data-context';
import {
  StatusBadge,
  EligibilityBadge,
  DecisionBadge,
  ValidityBadge,
} from '../components/ui/StatusBadge';
import { formatCurrency, formatDeadlineDays } from '../lib/formatters';

export const PortfolioPage: React.FC = () => {
  const { portfolio, tenders } = useData();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [activeSegment, setActiveSegment] = useState<
    'all' | 'in_progress' | 'attention' | 'eligible' | 'discarded'
  >('all');
  const [decisionFilter, setDecisionFilter] = useState<string>('all');
  const [eligibilityFilter, setEligibilityFilter] = useState<string>('all');
  const [validityFilter, setValidityFilter] = useState<string>('all');
  const [onlyBlockers, setOnlyBlockers] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 8; // Mayor densidad operativa

  // 27. Segmentación rápida (No son filtros, primera fila)
  const countAll = portfolio.length;
  const countAttention = portfolio.filter(
    (p) => p.hasBlockers || p.validity === 'REQUIRES_REANALYSIS'
  ).length;
  const countEligible = portfolio.filter(
    (p) => p.eligibility === 'POTENTIALLY_ELIGIBLE'
  ).length;
  const countDiscarded = portfolio.filter((p) => p.decision === 'DISCARD').length;
  const countInProgress = portfolio.filter(
    (p) => p.decision === 'REVIEW' || p.decision === 'UNDECIDED'
  ).length;

  const filteredItems = portfolio.filter((item) => {
    // Búsqueda en portfolio
    const matchesSearch =
      searchTerm === '' ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.contractingAuthority.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.fileReference.toLowerCase().includes(searchTerm.toLowerCase());

    // Segmentos superiores
    let matchesSegment = true;
    if (activeSegment === 'attention') {
      matchesSegment = item.hasBlockers || item.validity === 'REQUIRES_REANALYSIS';
    } else if (activeSegment === 'eligible') {
      matchesSegment = item.eligibility === 'POTENTIALLY_ELIGIBLE';
    } else if (activeSegment === 'discarded') {
      matchesSegment = item.decision === 'DISCARD';
    } else if (activeSegment === 'in_progress') {
      matchesSegment = item.decision === 'REVIEW' || item.decision === 'UNDECIDED';
    }

    // 28. Filtros operativos inferiores
    const matchesDecision =
      decisionFilter === 'all' || item.decision === decisionFilter;
    const matchesEligibility =
      eligibilityFilter === 'all' || item.eligibility === eligibilityFilter;
    const matchesValidity =
      validityFilter === 'all' || item.validity === validityFilter;
    const matchesBlockers = !onlyBlockers || item.hasBlockers;

    return (
      matchesSearch &&
      matchesSegment &&
      matchesDecision &&
      matchesEligibility &&
      matchesValidity &&
      matchesBlockers
    );
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-4 select-none">
      {/* HEADER PORTFOLIO */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <nav className="text-xs text-[#929097] flex items-center gap-1.5 mb-1.5">
            <Link to="/app/inicio" className="hover:text-[#171719] transition-colors">
              Inicio
            </Link>
            <span>›</span>
            <span className="text-[#171719] font-medium">Portfolio</span>
          </nav>

          <h1 className="app-page-title text-[#171719]">
            Portfolio
          </h1>
          <p className="text-xs sm:text-sm text-[#69666d] mt-1">
            Todas las oportunidades analizadas por tu organización.
          </p>
        </div>

        {/* Búsqueda dentro de la página (Sección 12: en Portfolio la búsqueda pertenece a la vista) */}
        <div className="relative w-full md:w-72">
          <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#929097]" />
          <input
            type="text"
            placeholder="Buscar en tu portfolio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-9 pr-3 bg-white/70 focus:bg-white border border-[rgba(30,24,38,0.06)] focus:border-[#685cff] rounded-[11px] text-xs text-[#171719] placeholder-[#929097] transition-all focus:outline-none shadow-xs"
          />
        </div>
      </div>

      {/* 27. FILA 1: SEGMENTOS PORTFOLIO (Sección 27) */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
        <button
          onClick={() => {
            setActiveSegment('all');
            setCurrentPage(1);
          }}
          className={`h-[34px] px-3.5 rounded-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeSegment === 'all'
              ? 'text-[#685cff] bg-[#eeeaff] border border-[#685cff]/20 font-semibold'
              : 'text-[#69666d] hover:text-[#171719] bg-white/60 hover:bg-white border border-transparent'
          }`}
        >
          Todas {countAll}
        </button>

        <button
          onClick={() => {
            setActiveSegment('in_progress');
            setCurrentPage(1);
          }}
          className={`h-[34px] px-3.5 rounded-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeSegment === 'in_progress'
              ? 'text-[#685cff] bg-[#eeeaff] border border-[#685cff]/20 font-semibold'
              : 'text-[#69666d] hover:text-[#171719] bg-white/60 hover:bg-white border border-transparent'
          }`}
        >
          En análisis {countInProgress}
        </button>

        <button
          onClick={() => {
            setActiveSegment('attention');
            setCurrentPage(1);
          }}
          className={`h-[34px] px-3.5 rounded-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeSegment === 'attention'
              ? 'text-[#e44848] bg-[#ffeded] border border-[#e44848]/25 font-semibold'
              : 'text-[#69666d] hover:text-[#e44848] bg-white/60 hover:bg-white border border-transparent'
          }`}
        >
          Requieren atención {countAttention}
        </button>

        <button
          onClick={() => {
            setActiveSegment('eligible');
            setCurrentPage(1);
          }}
          className={`h-[34px] px-3.5 rounded-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeSegment === 'eligible'
              ? 'text-[#218a58] bg-[#e8f7ef] border border-[#218a58]/25 font-semibold'
              : 'text-[#69666d] hover:text-[#218a58] bg-white/60 hover:bg-white border border-transparent'
          }`}
        >
          Elegibles {countEligible}
        </button>

        <button
          onClick={() => {
            setActiveSegment('discarded');
            setCurrentPage(1);
          }}
          className={`h-[34px] px-3.5 rounded-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeSegment === 'discarded'
              ? 'text-[#69666d] bg-[#efedef] border border-[rgba(30,24,38,0.12)] font-semibold'
              : 'text-[#69666d] hover:text-[#171719] bg-white/60 hover:bg-white border border-transparent'
          }`}
        >
          Descartadas {countDiscarded}
        </button>
      </div>

      {/* 28. FILA 2: FILTROS OPERATIVOS REALES (Sección 28) */}
      <div className="flex items-center gap-2 flex-wrap text-xs pt-0.5">
        {/* Decisión */}
        <select
          value={decisionFilter}
          onChange={(e) => {
            setDecisionFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="h-8 px-2.5 rounded-[8px] bg-white/70 hover:bg-white border border-[rgba(30,24,38,0.06)] text-[#171719] focus:outline-none cursor-pointer shadow-xs"
        >
          <option value="all">Decisión: Todas ▾</option>
          <option value="PURSUE">Pursue (Avanzar)</option>
          <option value="REVIEW">Review (En revisión)</option>
          <option value="DISCARD">Descartada</option>
          <option value="UNDECIDED">Sin decisión</option>
        </select>

        {/* Elegibilidad */}
        <select
          value={eligibilityFilter}
          onChange={(e) => {
            setEligibilityFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="h-8 px-2.5 rounded-[8px] bg-white/70 hover:bg-white border border-[rgba(30,24,38,0.06)] text-[#171719] focus:outline-none cursor-pointer shadow-xs"
        >
          <option value="all">Elegibilidad: Todas ▾</option>
          <option value="POTENTIALLY_ELIGIBLE">Potencialmente Elegible</option>
          <option value="NEEDS_REVIEW">Necesita revisión</option>
          <option value="POTENTIALLY_INELIGIBLE">Potencialmente Inelegible</option>
        </select>

        {/* Vigencia */}
        <select
          value={validityFilter}
          onChange={(e) => {
            setValidityFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="h-8 px-2.5 rounded-[8px] bg-white/70 hover:bg-white border border-[rgba(30,24,38,0.06)] text-[#171719] focus:outline-none cursor-pointer shadow-xs"
        >
          <option value="all">Vigencia: Todas ▾</option>
          <option value="VALID">Análisis Vigente</option>
          <option value="REQUIRES_REANALYSIS">Requiere reanálisis</option>
          <option value="STALE">Desactualizado</option>
        </select>

        {/* Con bloqueos Toggle */}
        <button
          onClick={() => {
            setOnlyBlockers(!onlyBlockers);
            setCurrentPage(1);
          }}
          className={`h-8 px-3 rounded-[8px] border transition-all cursor-pointer flex items-center gap-1.5 shadow-xs ${
            onlyBlockers
              ? 'bg-[#ffeded] border-[#e44848]/30 text-[#e44848] font-semibold'
              : 'bg-white/70 hover:bg-white border-[rgba(30,24,38,0.06)] text-[#69666d]'
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${
              onlyBlockers ? 'bg-[#e44848]' : 'bg-[#ca8517]'
            }`}
          />
          <span>Con bloqueos</span>
        </button>

        {(decisionFilter !== 'all' ||
          eligibilityFilter !== 'all' ||
          validityFilter !== 'all' ||
          onlyBlockers) && (
          <button
            onClick={() => {
              setDecisionFilter('all');
              setEligibilityFilter('all');
              setValidityFilter('all');
              setOnlyBlockers(false);
              setCurrentPage(1);
            }}
            className="text-[#685cff] hover:underline text-xs ml-2 cursor-pointer font-medium"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* 29 & 31. TABLA OPERATIVA DE ALTA DENSIDAD (Filas 50-58px, Sección 29) */}
      {filteredItems.length === 0 ? (
        <div className="surface p-12 rounded-[20px] text-center max-w-md mx-auto space-y-3">
          <div className="w-12 h-12 rounded-full bg-[#f5f1ed] text-[#69666d] flex items-center justify-center mx-auto text-xl">
            ◈
          </div>
          <h3 className="font-editorial text-2xl text-[#171719]">
            Todavía no has analizado ninguna oportunidad.
          </h3>
          <p className="text-xs text-[#69666d] leading-relaxed">
            Explora el catálogo oficial de licitaciones públicas y abre tu primer análisis.
          </p>
          <button
            onClick={() => navigate('/app/catalogo')}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-[11px] bg-[#171719] text-white text-xs font-semibold hover:bg-[#28282b] transition-colors cursor-pointer"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span>Explorar catálogo</span>
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Encabezado visible en desktop idéntico a Catálogo */}
          <div className="hidden lg:grid grid-cols-[56px_minmax(0,1.8fr)_100px_92px_104px_112px_76px_76px_98px] gap-2.5 px-3 text-[11px] font-mono uppercase text-[var(--ink-tertiary)] select-none">
            <span></span>
            <span>Licitación / Organismo</span>
            <span>Importe</span>
            <span>Plazo Límite</span>
            <span>Estado</span>
            <span>Elegibilidad</span>
            <span>Decisión</span>
            <span>Vigencia</span>
            <span className="text-right">Acción</span>
          </div>

          <div className="surface rounded-[18px] overflow-hidden divide-y divide-[rgba(30,24,38,0.055)] shadow-xs">
            {paginatedItems.map((item) => {
              const isCritical =
                item.hasBlockers || item.validity === 'REQUIRES_REANALYSIS';

              const deadlineDays = Math.ceil(
                (new Date(item.submissionDeadline).getTime() - Date.now()) /
                  (1000 * 60 * 60 * 24)
              );
              const isCriticalDeadline = deadlineDays <= 7 && deadlineDays > 0;
              const tender = tenders.find((t) => t.id === item.tenderId);
              const cpvTag = tender?.cpvCode ? tender.cpvCode.split(' · ')[0] : 'CPV 72200000';
              const cpvDesc = tender?.cpvCode ? tender.cpvCode.split(' · ')[1] : 'Servicios TIC';

              return (
                <div
                  key={item.id}
                  onClick={() => navigate(`/app/portfolio/${item.tenderId}`)}
                  className={`portfolio-row group cursor-pointer ${
                    isCritical ? 'critical' : ''
                  }`}
                >
                  {/* 1. Thumbnail 56x54px con degradado arquitectónico suave */}
                  <div className="w-[56px] h-[54px] rounded-[10px] bg-gradient-to-br from-[#f8f5f2] via-[#eee9f2] to-[#e7e1ff] border border-[rgba(30,24,38,0.06)] flex items-center justify-center text-[#685cff] shrink-0 relative">
                    <FileText className="w-5 h-5 opacity-70" />
                    {isCritical && (
                      <span
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#e44848] ring-2 ring-white"
                        title="Requiere atención o reanálisis"
                      />
                    )}
                  </div>

                  {/* 2. Título, Entidad y Tags (Idéntico a Catálogo) */}
                  <div className="min-w-0 pr-2">
                    <h3 className="text-xs sm:text-sm font-semibold text-[#171719] truncate group-hover:text-[#685cff] transition-colors leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#69666d] truncate mt-0.5">
                      {item.contractingAuthority}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-[#f5f1ed] text-[#69666d] border border-[rgba(30,24,38,0.06)]">
                        {item.fileReference}
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-[#f5f1ed] text-[#69666d] border border-[rgba(30,24,38,0.06)]">
                        {cpvTag}
                      </span>
                      <span className="text-[11px] text-[#929097] truncate hidden sm:inline">
                        {cpvDesc}
                      </span>
                      <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-white/80 text-[#685cff] border border-[#685cff]/20">
                        {item.evidenceCoveragePercentage}% evidencias
                      </span>
                    </div>
                  </div>

                  {/* 3. Importe en tabular-nums sin font-mono */}
                  <div className="hidden lg:block">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--ink)] tabular-nums">
                      {formatCurrency(item.budgetAmount)}
                    </span>
                    <span className="block text-[10px] text-[#929097]">
                      Presupuesto base
                    </span>
                  </div>

                  {/* 4. Plazo Crítico (Idéntico a Catálogo) */}
                  <div className="hidden lg:block">
                    <span className="text-xs text-[#171719] font-medium block">
                      {new Intl.DateTimeFormat('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      }).format(new Date(item.submissionDeadline))}
                    </span>
                    <span
                      className={`text-[11px] tabular-nums ${
                        isCriticalDeadline
                          ? 'text-[#e44848] font-semibold'
                          : 'text-[#69666d]'
                      }`}
                    >
                      {formatDeadlineDays(item.submissionDeadline).label}
                    </span>
                  </div>

                  {/* 5. Estado de Análisis */}
                  <div className="hidden lg:block">
                    {item.hasBlockers ? (
                      <StatusBadge tone="danger" icon="warning">
                        Requiere atención
                      </StatusBadge>
                    ) : item.validity === 'REQUIRES_REANALYSIS' ? (
                      <StatusBadge tone="warning" icon="warning">
                        Reanálisis
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="success" icon="check">
                        En curso
                      </StatusBadge>
                    )}
                  </div>

                  {/* 6. Elegibilidad */}
                  <div className="hidden lg:block">
                    <EligibilityBadge status={item.eligibility} />
                  </div>

                  {/* 7. Decisión Humana */}
                  <div className="hidden lg:block">
                    <DecisionBadge decision={item.decision} />
                  </div>

                  {/* 8. Vigencia / Cambio Documental */}
                  <div className="hidden lg:block">
                    <ValidityBadge validity={item.validity} />
                  </div>

                  {/* 9. Botón Ver Análisis (Píldora Idéntica a Catálogo) */}
                  <div className="text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/app/portfolio/${item.tenderId}`);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] bg-white group-hover:bg-[#171719] text-[#171719] group-hover:text-white border border-[rgba(30,24,38,0.12)] group-hover:border-[#171719] text-xs font-semibold transition-all shadow-xs cursor-pointer whitespace-nowrap"
                    >
                      <span>Ver análisis</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* PAGINACIÓN */}
            <div className="p-3 bg-[#f8f5f2]/70 border-t border-[rgba(30,24,38,0.055)] flex items-center justify-between text-xs text-[#69666d]">
              <div>
                Mostrando{' '}
                <span className="font-semibold text-[#171719]">
                  {filteredItems.length === 0
                    ? 0
                    : (currentPage - 1) * itemsPerPage + 1}
                  –{Math.min(currentPage * itemsPerPage, filteredItems.length)}
                </span>{' '}
                de{' '}
                <span className="font-semibold text-[#171719]">
                  {filteredItems.length}
                </span>{' '}
                resultados
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="w-7 h-7 rounded-[7px] border border-[rgba(30,24,38,0.06)] bg-white disabled:opacity-40 hover:bg-[#f5f1ed] text-[#171719] flex items-center justify-center cursor-pointer transition-colors"
                >
                  ‹
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-7 h-7 rounded-[7px] text-xs font-semibold transition-colors cursor-pointer ${
                      currentPage === page
                        ? 'bg-[#685cff] text-white shadow-xs'
                        : 'bg-white hover:bg-[#f5f1ed] text-[#171719] border border-[rgba(30,24,38,0.06)]'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="w-7 h-7 rounded-[7px] border border-[rgba(30,24,38,0.06)] bg-white disabled:opacity-40 hover:bg-[#f5f1ed] text-[#171719] flex items-center justify-center cursor-pointer transition-colors"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
